import { AlertCircle, ArrowUpRight } from 'lucide-react';
import { formatCurrency } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';

interface OutstandingPaymentsCardProps {
  amount?: number;
  count: number;
  clickable: boolean;
  isFetching: boolean;
  currencySymbol: string;
  onClick: () => void;
}

/** Compact stat card showing outstanding (unpaid) amount; opens the details modal when clickable. */
export function OutstandingPaymentsCard({ amount, count, clickable, isFetching, currencySymbol, onClick }: OutstandingPaymentsCardProps) {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={() => clickable && onClick()}
      disabled={!clickable}
      className={`bg-white rounded-[24px] border border-gray-100 p-4 h-full min-h-[120px] flex flex-col justify-between text-left transition-all duration-300 ${
        clickable
          ? 'hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(0,0,0,0.05)] cursor-pointer'
          : 'cursor-default'
      } ${isFetching ? 'opacity-60' : 'opacity-100'}`}
      data-test-id="dashboard-card-financial-outstanding"
    >
      <div className="flex items-center justify-between">
        <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
          <AlertCircle className="h-4 w-4" />
        </div>
        {clickable && <ArrowUpRight className="h-3.5 w-3.5 text-gray-300" />}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 truncate">
          {t('dashboard.financialStats.outstanding')}
        </p>
        <p className="text-xl font-bold text-gray-900 tracking-tight mt-1 truncate">
          {amount != null ? formatCurrency(amount, currencySymbol) : '—'}
        </p>
        {count > 0 && (
          <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mt-1.5 inline-block">
            {t('dashboard.financialStats.outstandingPending', { count })}
          </span>
        )}
      </div>
    </button>
  );
}
